import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import {
	Image,
	ProductImagePosition,
	LocaleCode,
	validateImageFile,
	transformProductImage,
	FieldError,
	PRODUCT_IMAGE_UPLOAD_MIN_WIDTH,
	PRODUCT_IMAGE_UPLOAD_MIN_HEIGHT,
	ServiceUnavailableError,
} from '@anticatrade/shared';
import { ImageBox, SortableImageList, ImageEditor, DropZone } from '@anticatrade/shared';
import { ProductContext, productContext } from './Product';
import SlSkeleton from '@shoelace-style/shoelace/dist/react/skeleton/index.js';
import SlButton from '@shoelace-style/shoelace/dist/react/button/index.js';
import SlAlert from '@shoelace-style/shoelace/dist/react/alert/index.js';
import SlIcon from '@shoelace-style/shoelace/dist/react/icon/index.js';
import { useTranslation } from 'react-i18next';
import { ErrorField } from '../../base/ErrorField/ErrorField';
import { LightBox } from '../../base/LightBox/LightBox';
import { RemoveBackgroundResponse } from '@anticatrade/admin';
import { AppContext, appContext } from '../../../context/AppContext';

interface ImageToEdit {
	url: string;
	position: number | null;
}

type ProductImageType = 'main' | 'gallery';

const ProductImages = () => {
	const [imageToEdit, setImageToEdit] = useState<ImageToEdit>();
	const [isImageEditorOpen, setIsImageEditorOpen] = useState<boolean>(false);
	const [lightboxIndex, setLightboxIndex] = useState<number>(0);
	const [isLightboxOpen, setIsLightboxOpen] = useState<boolean>(false);

	const { t, i18n } = useTranslation();
	const { api, handleError } = useContext(appContext as React.Context<AppContext>);
	const {
		originalImage,
		setOriginalImage,
		image,
		setImage,
		gallery,
		setGallery,
		retrievedProduct,
		isLoadingImages,
		isProcessingMainImage,
		setIsProcessingMainImage,
		galleryProcessingCount,
		setGalleryProcessingCount,
		useOriginalImage,
		setUseOriginalImage,
		bgRemovalUnavailable,
		setBgRemovalUnavailable,
		objectURLs,
		error,
		setError,
	} = useContext(productContext as React.Context<ProductContext>);

	const imageInputRef = useRef<any>();
	const galleryInputRef = useRef<any>();

	useEffect(() => {
		if (imageToEdit !== undefined) {
			setIsImageEditorOpen(true);
		} else {
			setIsImageEditorOpen(false);
		}
	}, [imageToEdit]);

	const lightboxImages = useMemo(() => {
		const images = [];
		if (image != null) {
			if (useOriginalImage) {
				images.push({ src: originalImage!.url, title: t('Main image') });
			} else {
				images.push({ src: image.url, title: t('Main image') });
			}
		}
		if (gallery != null) {
			let i = 1;
			for (const img of gallery.sort((a, b) => (a.position! < b.position! ? -1 : 1))) {
				images.push({ src: img.url, title: t('Gallery') + ` ${i}/${gallery.length}` });
				i++;
			}
		}
		return images;
	}, [image, gallery, useOriginalImage]);

	const onUploadMainImage = async (e: any, from: 'drop' | 'upload') => {
		setError(undefined);
		setUseOriginalImage(false);
		setBgRemovalUnavailable(false);

		let file: File | null;
		if (from === 'drop') {
			file = Array.from(e.dataTransfer.files)[0] as File;
		} else {
			file = Array.from(e.target.files)[0] as File;
		}
		if (file == null) {
			return;
		}

		const locale = i18n.language as LocaleCode;

		try {
			await validateImageFile(
				file,
				PRODUCT_IMAGE_UPLOAD_MIN_WIDTH,
				PRODUCT_IMAGE_UPLOAD_MIN_HEIGHT,
				true,
				true,
				locale,
			);
		} catch (err) {
			const er = err as Error;
			e.target.value = '';
			setTimeout(() => {
				setError(new FieldError(er.message, 'image'));
			}, 300);
			return;
		}

		setIsProcessingMainImage(true);

		let processed: File;
		try {
			processed = await transformProductImage(file, locale);
		} catch (err) {
			const er = err as Error;
			e.target.value = '';
			setTimeout(() => {
				setIsProcessingMainImage(false);
				setError(new FieldError(er.message, 'image'));
			}, 300);
			return;
		}

		const originalFileURL: string = URL.createObjectURL(processed);
		objectURLs.current.push(originalFileURL);
		setOriginalImage({
			file: processed,
			url: originalFileURL,
			position: null,
		});

		let res: RemoveBackgroundResponse;
		try {
			res = await api.removeBackground(processed);
		} catch (err) {
			e.target.value = '';
			if (err instanceof ServiceUnavailableError) {
				// Photoroom outage: keep the original image as the only
				// version (clear `originalImage` so the version-toggle
				// stays hidden and drafts don't persist the same file
				// twice) and warn the merchant instead of blocking.
				setTimeout(() => {
					setImage({ file: processed, url: originalFileURL, position: null });
					setOriginalImage(undefined);
					setBgRemovalUnavailable(true);
					setIsProcessingMainImage(false);
				}, 300);
				return;
			}
			setTimeout(() => {
				setOriginalImage(undefined);
				setIsProcessingMainImage(false);
				handleError(err as Error);
			}, 300);
			return;
		}

		const blob = new Blob([Uint8Array.from(atob(res.image), (c) => c.charCodeAt(0))], {
			type: 'image/webp',
		});
		const f = new File([blob], 'background-removed.webp', { type: 'image/webp' });

		const fileURL: string = URL.createObjectURL(f);
		objectURLs.current.push(fileURL);
		setImage({ file: f, url: fileURL, position: null });
		setIsProcessingMainImage(false);
	};

	const onUploadGallery = async (e: any, from: 'drop' | 'upload') => {
		setError(undefined);

		let files: File[] | null;
		if (from === 'drop') {
			files = Array.from(e.dataTransfer.files);
		} else {
			files = Array.from(e.target.files);
		}
		if (files.length === 0) {
			return;
		}

		let lastPosition = 0;
		let maxImageConstented = 12;
		const hasAlreadyImages = gallery != undefined;
		if (hasAlreadyImages) {
			if (gallery.length >= 12) {
				setError(
					new FieldError(
						t('You can upload at most {max} images').replace('{max}', String(12)),
						'gallery',
					),
				);
				return;
			}
			maxImageConstented = maxImageConstented - gallery.length;
			for (const image of gallery) {
				if (image.position! > lastPosition) {
					lastPosition = image.position!;
				}
			}
		}

		let toUpload = files.slice(0, maxImageConstented);
		setGalleryProcessingCount(toUpload.length);

		const locale = i18n.language as LocaleCode;
		let images: Image[] = [];
		for (const f of toUpload) {
			try {
				await validateImageFile(
					f,
					PRODUCT_IMAGE_UPLOAD_MIN_WIDTH,
					PRODUCT_IMAGE_UPLOAD_MIN_HEIGHT,
					true,
					true,
					locale,
				);
			} catch (err) {
				const er = err as Error;
				e.target.value = '';
				setTimeout(() => {
					setGalleryProcessingCount(0);
					setError(new FieldError(er.message, 'gallery'));
					return;
				}, 300);
				return;
			}

			let processed: File;
			try {
				processed = await transformProductImage(f, locale);
			} catch (err) {
				const er = err as Error;
				e.target.value = '';
				setTimeout(() => {
					setGalleryProcessingCount(0);
					setError(new FieldError(er.message, 'gallery'));
				}, 300);
				return;
			}

			const fileURL: string = URL.createObjectURL(processed);
			objectURLs.current.push(fileURL);
			images.push({
				file: processed,
				url: fileURL,
				position: lastPosition + 1,
			});
			lastPosition++;
		}

		if (hasAlreadyImages) {
			images = [...gallery, ...images];
		}
		setGallery(images);
		setGalleryProcessingCount(0);
		if (files.length > maxImageConstented) {
			setError(
				new FieldError(
					t('You can upload at most {max} images').replace('{max}', String(12)),
					'gallery',
				),
			);
		}
	};

	const onProcessImage = async (e: any) => {
		const locale = i18n.language as LocaleCode;
		try {
			await validateImageFile(
				e.dest,
				PRODUCT_IMAGE_UPLOAD_MIN_WIDTH,
				PRODUCT_IMAGE_UPLOAD_MIN_HEIGHT,
				true,
				false,
				locale,
				true,
			);
		} catch (err) {
			// Avoid saving the processed image as it violates
			// validation.
			const er = err as Error;
			handleError(`${t('The modified image could not be saved')}: ${er.message}`);
			setImageToEdit(undefined);
			return;
		}
		const fileURL: string = URL.createObjectURL(e.dest);
		objectURLs.current.push(fileURL);
		if (imageToEdit!.position == null) {
			if (useOriginalImage) {
				setOriginalImage({ file: e.dest, url: fileURL, position: null });
			} else {
				setImage({ file: e.dest, url: fileURL, position: null });
			}
		} else {
			const g = [...gallery!];
			const index = g.findIndex((img) => img.position === imageToEdit!.position);
			g[index] = { file: e.dest, url: fileURL, position: imageToEdit!.position };
			setGallery(g);
		}
		setImageToEdit(undefined);
	};

	const onAddImage = (type: ProductImageType) => {
		if (type === 'main') {
			imageInputRef.current.click();
		} else if (type === 'gallery') {
			galleryInputRef.current.click();
		}
	};

	const onEditImage = (image: Image) => {
		setImageToEdit({
			url: image.url,
			position: image.position,
		});
	};

	const onSeeFullscreenImage = (position: ProductImagePosition) => {
		let index: number;
		if (position == null) {
			index = 0;
		} else {
			let galleryIndex = gallery!
				.sort((a, b) => (a.position! < b.position! ? -1 : 1))
				.findIndex((img) => img.position === position);
			index = galleryIndex + 1;
		}
		setLightboxIndex(index);
		setIsLightboxOpen(true);
	};

	const onRemoveImage = (position: ProductImagePosition) => {
		if (position == null) {
			imageInputRef.current.value = '';
			setImage(undefined);
			setOriginalImage(undefined);
			setUseOriginalImage(false);
			setBgRemovalUnavailable(false);
		} else {
			galleryInputRef.current.value = '';
			const g = [...gallery!];
			const index = gallery!.findIndex((img) => img.position === position);
			g.splice(index, 1);
			setGallery(g);
		}
	};

	const onSortImage = (overID: number, movedId: number) => {
		let images = [...gallery!];
		const overSlideIndex = images.findIndex((img) => img.position === overID);
		const imageToMove = images.find((img) => img.position === movedId);
		const gf = images.filter((img) => img.position !== movedId);
		gf.splice(overSlideIndex, 0, imageToMove!);
		// re-compute the positions.
		let lastPosition = 1;
		for (const i of gf) {
			i.position = lastPosition;
			lastPosition++;
		}
		setGallery(gf);
	};

	return (
		<div className='product__section product__section--images'>
			<div className='product__image-editor-wrapper'>
				<ImageEditor
					src={imageToEdit?.url}
					onProcess={onProcessImage}
					isOpen={isImageEditorOpen}
					setIsOpen={setIsImageEditorOpen}
					locale={i18n.language as LocaleCode}
					t={t}
				/>
			</div>
			<LightBox
				images={lightboxImages}
				isOpen={isLightboxOpen}
				index={lightboxIndex}
				closeFunc={() => setIsLightboxOpen(false)}
			/>
			<div className='product__section-form wrapped'>
				<div className='product__section-title'>{t('Images')}</div>
				<ErrorField name='image' error={error}>
					<div className='product__image'>
						<div className='product__image-title'>
							{t('Main image')}
							<span className='product__image-title-asterisk'>*</span>
						</div>
						{isLoadingImages || isProcessingMainImage ? (
							<SlSkeleton className='product__image-skeleton' effect='sheen' />
						) : image ? (
							<div>
								<ImageBox
									url={useOriginalImage ? originalImage!.url : image.url}
									position={null}
									onEditImage={
										image.file != null
											? () =>
													onEditImage(
														useOriginalImage ? originalImage! : image,
													)
											: undefined
									}
									onRemoveImage={() => onRemoveImage(null)}
									onSeeFullscreenImage={() => onSeeFullscreenImage(null)}
									t={t}
								/>
								{bgRemovalUnavailable ? (
									<SlAlert
										className='product__image-original-alert'
										open
										variant='warning'
									>
										<SlIcon name='exclamation-triangle' slot='icon' />
										{t('Background removal service unavailable alert')}
									</SlAlert>
								) : useOriginalImage ? (
									<div className='product__image-version-original'>
										<SlButton
											className='product__image-change-version'
											onClick={() => setUseOriginalImage(false)}
										>
											{t('Use version without background')}
										</SlButton>

										<SlAlert
											className='product__image-original-alert'
											open
											variant='warning'
										>
											<SlIcon name='exclamation-triangle' slot='icon' />
											{t('Original image alert')}
										</SlAlert>
									</div>
								) : (
									originalImage != null && (
										<SlButton
											className='product__image-change-version'
											onClick={() => setUseOriginalImage(true)}
										>
											{t('Use original background')}
										</SlButton>
									)
								)}
							</div>
						) : (
							<div className='product__image-upload'>
								<DropZone
									multiple={false}
									onDropFile={(e) => onUploadMainImage(e, 'drop')}
									onClick={() => onAddImage('main')}
									t={t}
								/>
							</div>
						)}
						<input
							className='product__image-input'
							ref={imageInputRef}
							type='file'
							accept='image/jpeg, image/png, image/webp'
							onChange={(e) => onUploadMainImage(e, 'upload')}
							multiple={false}
						/>
					</div>
				</ErrorField>
				<ErrorField name='gallery' error={error}>
					<div className='product__gallery'>
						<div className='product__gallery-title'>
							{t('Gallery')}
						</div>

						{isLoadingImages ||
						((gallery == null || gallery.length === 0) &&
							galleryProcessingCount > 0) ? (
							<div className='product__gallery-skeletons'>
								{galleryProcessingCount > 0
									? Array.from({
											length: galleryProcessingCount,
										}).map(() => (
											<SlSkeleton
												className='product__image-skeleton'
												effect='sheen'
											/>
										))
									: retrievedProduct!.gallery.map(() => (
											<SlSkeleton
												className='product__image-skeleton'
												effect='sheen'
											/>
										))}
							</div>
						) : gallery != undefined && gallery.length > 0 ? (
							<div className='product__gallery-wrapper'>
								<SortableImageList
									images={gallery}
									className='product__gallery-images'
									onEditImage={onEditImage}
									onRemoveImage={onRemoveImage}
									onSortImage={onSortImage}
									onSeeFullscreenImage={onSeeFullscreenImage}
									dropZone={
										gallery.length >= 12 ||
										galleryProcessingCount > 0 ? null : (
											<DropZone
												multiple={true}
												isCompact={true}
												onDropFile={(e) => onUploadGallery(e, 'drop')}
												onClick={() => onAddImage('gallery')}
												t={t}
											/>
										)
									}
									loadingImages={
										galleryProcessingCount > 0
											? Array.from({
													length: galleryProcessingCount,
												}).map(() => (
													<SlSkeleton
														className='product__image-skeleton'
														effect='sheen'
													/>
												))
											: null
									}
									t={t}
								/>
							</div>
						) : (
							<div className='product__gallery-upload'>
								<DropZone
									multiple={true}
									onDropFile={(e) => onUploadGallery(e, 'drop')}
									onClick={() => onAddImage('gallery')}
									t={t}
								/>
							</div>
						)}
						<input
							className='product__gallery-input'
							ref={galleryInputRef}
							type='file'
							accept='image/jpeg, image/png, image/webp'
							onChange={(e) => onUploadGallery(e, 'upload')}
							multiple={true}
						/>
					</div>
				</ErrorField>
			</div>
		</div>
	);
};

export { ProductImages };
export type { ImageToEdit };
